import {useState, type FormEvent} from 'react'
import {useNavigate, useParams} from 'react-router-dom'
import {Lock, Eye, EyeOff, Loader2, AlertCircle, CheckCircle, ArrowLeft} from 'lucide-react'
import {sha256} from 'crypto-hash'
import * as API from '../../../api'
import {toAbsoluteUrl} from '../../../../_metronic/helpers'


type RouteParams = {
  token?: string
  userGuid?: string
  email?: string
}

const MIN_PASSWORD = 6

export function ResetPasswordForm() {
  const navigate = useNavigate()
  const {token, userGuid, email} = useParams<RouteParams>()

  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')

  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)

  const [touched, setTouched] = useState({password: false, confirmPassword: false})

  const passwordError =
    touched.password && !password
      ? 'O preenchimento da senha é necessário'
      : touched.password && password.length > 0 && password.length < MIN_PASSWORD
        ? `Mínimo de ${MIN_PASSWORD} caracteres`
        : ''

  const confirmPasswordError =
    touched.confirmPassword && !confirmPassword
      ? 'O preenchimento da confirmação de senha é necessário'
      : touched.confirmPassword && confirmPassword.length > 0 && confirmPassword.length < MIN_PASSWORD
        ? `Mínimo de ${MIN_PASSWORD} caracteres`
        : touched.confirmPassword && password && confirmPassword && password !== confirmPassword
          ? 'A confirmação de senha deve ser igual à senha'
          : ''

  const isValid =
    password.length >= MIN_PASSWORD &&
    confirmPassword.length >= MIN_PASSWORD &&
    password === confirmPassword

  const handleBackToLogin = () => navigate('/auth/login')

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setTouched({password: true, confirmPassword: true})

    if (!isValid || loading) return

    setLoading(true)
    setError('')
    setSuccess(false)

    try {
      // validações de params (erros já tratados no legado)
      if (!token) throw new Error('Link inválido ou expirado. Solicite uma nova redefinição de senha.')
      if (!userGuid) throw new Error('Não foi possível identificar o usuário. Solicite uma nova redefinição de senha.')
      if (!email) throw new Error('Não foi possível identificar o e-mail. Solicite uma nova redefinição de senha.')

      const passwordHash = await sha256(password)

      const res: any = await API.resetPassword(token, userGuid, email, passwordHash)

      // fallback seguro caso o endpoint retorne {success:false}
      if (res?.success === false) {
        setError('Não foi possível redefinir sua senha. Por favor, tente novamente.')
        setLoading(false)
        return
      }

      setSuccess(true)
      // redireciona depois de um tempinho, mantendo a experiência do legado
      setTimeout(() => navigate('/auth/login'), 1000)
    } catch (err: any) {
      setError(err?.message || 'Desculpe, ocorreu um erro. Por favor, tente novamente.')
    } finally {
      setLoading(false)
    }
  }

  if (success) {
    return (
      <div
        className="w-full max-w-[520px] animate-card-entry translate-y-4 text-[1.05rem] leading-relaxed font-[-apple-system,BlinkMacSystemFont,'Segoe_UI',Roboto,'Helvetica_Neue',Arial,sans-serif] box-border"
        role="main"
      >
        <div className="bg-white/[0.92] backdrop-blur-[24px] border border-white/70 rounded-[22px] px-8 py-10 sm:px-11 sm:py-12 shadow-[0_6px_10px_-2px_rgba(0,50,120,0.05),0_28px_55px_-10px_rgba(0,50,120,0.10),0_0_0_1px_rgba(0,80,180,0.03)]">
          <div className="flex flex-col items-center mb-8">
            <img
              src={toAbsoluteUrl('/media/logos/logo-wide.png')}
              alt="Compra Agora Interno - Logo"
              className="h-14 w-auto mb-3 object-contain"
            />
            <p className="text-sm sm:text-[0.95rem] text-slate-500 text-center">
              Redefinição de senha
            </p>
          </div>

          <div
            className="flex items-start gap-3 px-5 py-4 mb-6 bg-emerald-50 border border-emerald-200 rounded-xl leading-normal"
            role="status"
          >
            <CheckCircle size={22} className="shrink-0 text-emerald-600 mt-0.5" />
            <div>
              <p className="font-semibold text-emerald-900 text-[0.98rem]">
                Senha alterada com sucesso
              </p>
              <p className="text-emerald-800 text-[0.92rem] mt-1 leading-relaxed">
                Você será redirecionado para o login.
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={handleBackToLogin}
            className="login-submit w-full py-4 mt-2 text-[1.05rem] font-semibold text-white bg-gradient-to-br from-[#0066ff] to-[#0055dd] border-none rounded-xl cursor-pointer transition-all duration-200 relative overflow-hidden leading-normal shadow-[0_10px_20px_-10px_rgba(0,102,255,0.65)] hover:shadow-[0_16px_28px_-14px_rgba(0,102,255,0.75)] hover:-translate-y-[1px] active:translate-y-0"
          >
            Ir para o login
          </button>

          <div className="mt-7 pt-6 border-t border-slate-100">
            <p className="text-[0.9rem] text-slate-400 text-center font-light">
              Acesso restrito a usuários autorizados
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      className="w-full max-w-[520px] animate-card-entry translate-y-4 text-[1.05rem] leading-relaxed font-[-apple-system,BlinkMacSystemFont,'Segoe_UI',Roboto,'Helvetica_Neue',Arial,sans-serif] box-border"
      role="main"
    >
      <div className="bg-white/[0.92] backdrop-blur-[24px] border border-white/70 rounded-[22px] px-8 py-10 sm:px-11 sm:py-12 shadow-[0_6px_10px_-2px_rgba(0,50,120,0.05),0_28px_55px_-10px_rgba(0,50,120,0.10),0_0_0_1px_rgba(0,80,180,0.03)]">
        <div className="flex flex-col items-center mb-8">
          <img
            src={toAbsoluteUrl('/media/logos/logo-wide.png')}
            alt="Compra Agora Interno - Logo"
            className="h-14 w-auto mb-3 object-contain"
          />
          <p className="text-sm sm:text-[0.95rem] text-slate-500 text-center">
            Redefinição de senha
          </p>
        </div>

        <div className="text-center mb-7">
          <h1 className="text-[1.2rem] font-semibold text-slate-900">
            Defina uma nova senha
          </h1>
          <p className="text-[0.95rem] text-slate-500 mt-2 font-light leading-relaxed px-2">
            Digite e confirme sua nova senha para continuar.
          </p>
        </div>

        {error && (
          <div
            className="flex items-center gap-2.5 px-4 py-3.5 mb-6 text-[0.95rem] text-red-700 bg-red-50 border border-red-200 rounded-xl leading-snug"
            role="alert"
          >
            <AlertCircle size={18} className="shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate className="space-y-6">
          {/* Password */}
          <div className="flex flex-col">
            <label
              htmlFor="new-password"
              className="block text-[0.95rem] font-semibold text-slate-700 mb-2 leading-snug"
            >
              Nova senha
            </label>

            <div
              className={`login-wrapper ${passwordError ? 'wrapper-error' : ''} relative flex items-center bg-slate-50/80 rounded-xl transition-all duration-200 ${
                passwordError
                  ? 'border-[1.5px] border-red-500'
                  : 'border-[1.5px] border-slate-200 hover:border-slate-300 focus-within:border-blue-400 focus-within:ring-4 focus-within:ring-blue-100/70'
              }`}
            >
              <Lock
                size={20}
                className="login-icon absolute left-4 text-slate-400 pointer-events-none transition-colors duration-200"
              />
              <input
                id="new-password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Digite sua nova senha"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onBlur={() => setTouched((p) => ({...p, password: true}))}
                autoComplete="new-password"
                aria-invalid={!!passwordError}
                aria-describedby={passwordError ? 'new-password-error' : undefined}
                className="login-input w-full py-4 pl-12 pr-12 text-[1rem] text-slate-800 bg-transparent border-none outline-none leading-normal placeholder:text-slate-400"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="login-pass-toggle absolute right-3 flex items-center justify-center p-2 rounded-lg text-slate-500 hover:text-slate-700 hover:bg-white/70 bg-transparent border-none cursor-pointer transition-all duration-150 focus:outline-none focus-visible:ring-4 focus-visible:ring-blue-100/80"
                aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>

            {passwordError && (
              <p
                id="new-password-error"
                className="text-[0.9rem] text-red-600 mt-2 pl-0.5 leading-snug"
                role="alert"
              >
                {passwordError}
              </p>
            )}
          </div>

          {/* Confirm Password */}
          <div className="flex flex-col">
            <label
              htmlFor="confirm-password"
              className="block text-[0.95rem] font-semibold text-slate-700 mb-2 leading-snug"
            >
              Confirmar nova senha
            </label>

            <div
              className={`login-wrapper ${confirmPasswordError ? 'wrapper-error' : ''} relative flex items-center bg-slate-50/80 rounded-xl transition-all duration-200 ${
                confirmPasswordError
                  ? 'border-[1.5px] border-red-500'
                  : 'border-[1.5px] border-slate-200 hover:border-slate-300 focus-within:border-blue-400 focus-within:ring-4 focus-within:ring-blue-100/70'
              }`}
            >
              <Lock
                size={20}
                className="login-icon absolute left-4 text-slate-400 pointer-events-none transition-colors duration-200"
              />
              <input
                id="confirm-password"
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder="Confirme sua nova senha"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                onBlur={() => setTouched((p) => ({...p, confirmPassword: true}))}
                autoComplete="new-password"
                aria-invalid={!!confirmPasswordError}
                aria-describedby={confirmPasswordError ? 'confirm-password-error' : undefined}
                className="login-input w-full py-4 pl-12 pr-12 text-[1rem] text-slate-800 bg-transparent border-none outline-none leading-normal placeholder:text-slate-400"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword((v) => !v)}
                className="login-pass-toggle absolute right-3 flex items-center justify-center p-2 rounded-lg text-slate-500 hover:text-slate-700 hover:bg-white/70 bg-transparent border-none cursor-pointer transition-all duration-150 focus:outline-none focus-visible:ring-4 focus-visible:ring-blue-100/80"
                aria-label={showConfirmPassword ? 'Ocultar senha' : 'Mostrar senha'}
              >
                {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>

            {confirmPasswordError && (
              <p
                id="confirm-password-error"
                className="text-[0.9rem] text-red-600 mt-2 pl-0.5 leading-snug"
                role="alert"
              >
                {confirmPasswordError}
              </p>
            )}
          </div>

          <div className="flex flex-col gap-3">
            <button
              type="submit"
              disabled={loading || !isValid}
              className={`login-submit w-full py-4 text-[1.05rem] font-semibold text-white bg-gradient-to-br from-[#0066ff] to-[#0055dd] border-none rounded-xl cursor-pointer transition-all duration-200 relative overflow-hidden leading-normal shadow-[0_10px_20px_-10px_rgba(0,102,255,0.65)] hover:shadow-[0_16px_28px_-14px_rgba(0,102,255,0.75)] hover:-translate-y-[1px] active:translate-y-0 ${
                loading || !isValid
                  ? 'opacity-70 cursor-not-allowed hover:translate-y-0'
                  : ''
              }`}
              aria-busy={loading}
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader2 size={20} className="animate-spin" />
                  Aguarde, por favor...
                </span>
              ) : (
                'Alterar senha'
              )}
            </button>

            <button
              type="button"
              onClick={handleBackToLogin}
              className="login-back flex items-center justify-center gap-2 w-full py-4 text-[1rem] font-semibold text-slate-600 bg-white/50 hover:bg-white/70 border-[1.5px] border-slate-200 rounded-xl cursor-pointer transition-all duration-200 leading-normal focus:outline-none focus-visible:ring-4 focus-visible:ring-blue-100/80"
            >
              <ArrowLeft size={18} />
              Voltar para o login
            </button>
          </div>
        </form>

        <div className="mt-7 pt-6 border-t border-slate-100">
          <p className="text-[0.9rem] text-slate-400 text-center font-light">
            Acesso restrito a usuários autorizados
          </p>
        </div>
      </div>
    </div>
  )
}
